import type { SpatialObjectKind, WorkspaceId } from '@/types';
import type { ApplicationRegistry } from '@/applications';
import type { SceneGraph } from '@/core/scene/sceneGraph';
import { OBJECT_KINDS, objectType } from '@/core/scene/objectTypes';
import { useWorkspaceStore, selectActiveDocument } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useCameraStore } from '@/store/cameraStore';
import { useSettingsStore } from '@/store/settingsStore';
import {
  addObject,
  connectSelection,
  deleteSelection,
  duplicateSelection,
  groupSelection,
  setSelectionCollapsed,
  ungroupSelection,
} from '@/features/objects/objectActions';
import { CONNECTION_TYPES } from '@/types';
import { useDialogStore } from '@/store/dialogStore';
import { buildShowcaseWorkspace } from '@/features/showcase/showcaseWorkspace';
import { asWorkspaceId } from '@/types';
import { createId } from '@/utils/id';
import { openApplication } from '@/features/windows/windowManager';
import {
  centerOnSelection,
  goToCamera,
  zoomToContent,
  zoomToSelection,
  zoomToWorkspace,
} from '@/features/camera/cameraNavigation';
import { addBookmarkCommand, removeBookmarkCommand } from '@/features/camera/bookmarkCommands';
import type { CameraAnimator } from '@/features/camera/cameraAnimator';

/**
 * Everything the command palette can do, as data. Commands may expose `children`
 * (a second level, e.g. which kind to create) or `mode: 'search'` (hand the query to
 * spatial search). The palette UI only renders and runs; all behaviour lives here so
 * it is testable without React.
 */
export interface PaletteCommand {
  readonly id: string;
  readonly title: string;
  readonly group: string;
  readonly keywords?: readonly string[];
  readonly shortcut?: string;
  readonly enabled?: () => boolean;
  readonly run?: () => void;
  readonly children?: () => readonly PaletteCommand[];
  readonly mode?: 'search';
}

export interface PaletteContext {
  readonly scene: SceneGraph;
  readonly applications: ApplicationRegistry;
  readonly animator: CameraAnimator;
}

const hasSelection = () => useSelectionStore.getState().selection.objectIds.length > 0;
const doc = () => selectActiveDocument(useWorkspaceStore.getState());

export const buildPaletteCommands = ({ scene, applications, animator }: PaletteContext): readonly PaletteCommand[] => {
  const nav = (
    id: string,
    title: string,
    run: () => void,
    shortcut?: string,
    enabled?: () => boolean,
  ): PaletteCommand => ({
    id,
    title,
    group: 'Navigate',
    ...(shortcut ? { shortcut } : {}),
    ...(enabled ? { enabled } : {}),
    run,
  });

  return [
    {
      id: 'create',
      title: 'Create…',
      group: 'Objects',
      keywords: ['new', 'add', 'insert'],
      children: () =>
        OBJECT_KINDS.map((kind: SpatialObjectKind) => ({
          id: `create.${kind}`,
          title: `Create ${objectType(kind).label}`,
          group: 'Create',
          keywords: [kind],
          run: () => {
            addObject(kind, applications);
          },
        })),
    },
    {
      id: 'open',
      title: 'Open application…',
      group: 'Applications',
      keywords: ['launch', 'app', 'run'],
      children: () =>
        applications.list().map((app) => ({
          id: `open.${app.id}`,
          title: `Open ${app.name}`,
          group: 'Open',
          keywords: [app.category, app.description],
          run: () => {
            openApplication(app.id, applications);
          },
        })),
    },
    {
      id: 'search',
      title: 'Search…',
      group: 'Navigate',
      keywords: ['find', 'go to', 'jump'],
      shortcut: '⌘F',
      mode: 'search',
    },

    nav(
      'zoom.selection',
      'Zoom to Selection',
      () => {
        zoomToSelection(scene, animator);
      },
      '⇧2',
      hasSelection,
    ),
    nav(
      'zoom.content',
      'Zoom to Content',
      () => {
        zoomToContent(scene, animator);
      },
      '⇧1',
    ),
    nav(
      'zoom.workspace',
      'Zoom to Workspace',
      () => {
        zoomToWorkspace(scene, animator);
      },
      '⇧3',
    ),
    nav(
      'center.selection',
      'Center Selection',
      () => {
        centerOnSelection(scene, animator);
      },
      '⇧4',
      hasSelection,
    ),
    nav(
      'zoom.reset',
      'Reset Zoom to 100%',
      () => {
        useCameraStore.getState().setZoom(1);
      },
      '⌘0',
    ),
    {
      id: 'bookmark.add',
      title: 'Bookmark This View',
      group: 'Navigate',
      keywords: ['save view', 'camera'],
      shortcut: '⇧⌘B',
      run: () => {
        const d = doc();
        if (d)
          useWorkspaceStore
            .getState()
            .dispatch(
              addBookmarkCommand(d, useCameraStore.getState().camera, `View ${d.workspace.bookmarks.length + 1}`),
            );
      },
    },
    {
      id: 'bookmark.go',
      title: 'Go to Bookmark…',
      group: 'Navigate',
      keywords: ['view', 'jump'],
      enabled: () => (doc()?.workspace.bookmarks.length ?? 0) > 0,
      children: () =>
        (doc()?.workspace.bookmarks ?? []).map((b) => ({
          id: `bookmark.go.${b.id}`,
          title: b.name,
          group: 'Bookmarks',
          keywords: [`${Math.round(b.camera.zoom * 100)}%`],
          run: () => {
            goToCamera(b.camera, animator);
          },
        })),
    },
    {
      id: 'bookmark.remove',
      title: 'Remove Bookmark…',
      group: 'Navigate',
      enabled: () => (doc()?.workspace.bookmarks.length ?? 0) > 0,
      children: () =>
        (doc()?.workspace.bookmarks ?? []).map((b) => ({
          id: `bookmark.remove.${b.id}`,
          title: `Remove "${b.name}"`,
          group: 'Bookmarks',
          run: () => {
            const d = doc();
            const cmd = d && removeBookmarkCommand(d, b.id);
            if (cmd) useWorkspaceStore.getState().dispatch(cmd);
          },
        })),
    },

    {
      id: 'group',
      title: 'Group Selection',
      group: 'Objects',
      shortcut: '⌘G',
      enabled: hasSelection,
      run: () => {
        groupSelection(scene);
      },
    },
    {
      id: 'ungroup',
      title: 'Ungroup Selection',
      group: 'Objects',
      shortcut: '⇧⌘G',
      enabled: hasSelection,
      run: () => {
        ungroupSelection(scene);
      },
    },
    {
      id: 'collapse',
      title: 'Collapse Group',
      group: 'Objects',
      keywords: ['fold'],
      enabled: hasSelection,
      run: () => {
        setSelectionCollapsed(true);
      },
    },
    {
      id: 'expand',
      title: 'Expand Group',
      group: 'Objects',
      keywords: ['unfold'],
      enabled: hasSelection,
      run: () => {
        setSelectionCollapsed(false);
      },
    },
    {
      id: 'connect',
      title: 'Connect Selection…',
      group: 'Objects',
      keywords: ['link', 'edge', 'relationship'],
      enabled: () => useSelectionStore.getState().selection.objectIds.length >= 2,
      children: () =>
        CONNECTION_TYPES.map((type) => ({
          id: `connect.${type}`,
          title: `Connect: ${type}`,
          group: 'Connect',
          run: () => {
            connectSelection(type);
          },
        })),
    },
    {
      id: 'duplicate',
      title: 'Duplicate Selection',
      group: 'Objects',
      shortcut: '⌘D',
      enabled: hasSelection,
      run: duplicateSelection,
    },
    {
      id: 'delete',
      title: 'Delete Selection',
      group: 'Objects',
      shortcut: '⌫',
      enabled: hasSelection,
      run: deleteSelection,
    },
    {
      id: 'undo',
      title: 'Undo',
      group: 'Edit',
      shortcut: '⌘Z',
      enabled: () => {
        const s = useWorkspaceStore.getState();
        return (s.histories[s.activeWorkspaceId]?.past.length ?? 0) > 0;
      },
      run: () => {
        useWorkspaceStore.getState().undo();
      },
    },
    {
      id: 'redo',
      title: 'Redo',
      group: 'Edit',
      shortcut: '⇧⌘Z',
      enabled: () => {
        const s = useWorkspaceStore.getState();
        return (s.histories[s.activeWorkspaceId]?.future.length ?? 0) > 0;
      },
      run: () => {
        useWorkspaceStore.getState().redo();
      },
    },
    {
      id: 'toggle.grid',
      title: 'Toggle Grid',
      group: 'View',
      keywords: ['background', 'lines'],
      shortcut: "⌘'",
      run: () => {
        useSettingsStore.getState().setGridVisible(!useSettingsStore.getState().gridVisible);
      },
    },
    {
      id: 'toggle.minimap',
      title: 'Toggle Minimap',
      group: 'View',
      keywords: ['overview', 'map'],
      shortcut: '⇧⌘M',
      run: () => {
        useSettingsStore.getState().setMinimapVisible(!useSettingsStore.getState().minimapVisible);
      },
    },
    {
      id: 'toggle.developer',
      title: 'Toggle Developer Mode',
      group: 'View',
      keywords: ['debug', 'diagnostics', 'stats', 'fps'],
      shortcut: '⇧⌘D',
      run: () => {
        useSettingsStore.getState().setDeveloperMode(!useSettingsStore.getState().developerMode);
      },
    },
    {
      id: 'workspace.new',
      title: 'New Workspace',
      group: 'Workspace',
      keywords: ['create', 'add'],
      run: () => {
        const s = useWorkspaceStore.getState();
        s.createWorkspace(`Workspace ${Object.keys(s.workspaces).length + 1}`);
      },
    },
    {
      id: 'workspace.showcase',
      title: 'Add Showcase Workspace',
      group: 'Workspace',
      keywords: ['example', 'demo', 'sample'],
      run: () => {
        useWorkspaceStore.getState().importWorkspace(buildShowcaseWorkspace(asWorkspaceId(createId('ws'))));
      },
    },
    {
      id: 'workspace.rename',
      title: 'Rename Workspace…',
      group: 'Workspace',
      keywords: ['name', 'title'],
      run: () => {
        useDialogStore
          .getState()
          .open({ kind: 'rename-workspace', id: useWorkspaceStore.getState().activeWorkspaceId });
      },
    },
    {
      id: 'workspace.delete',
      title: 'Delete Workspace…',
      group: 'Workspace',
      keywords: ['remove'],
      enabled: () => Object.keys(useWorkspaceStore.getState().workspaces).length > 1,
      run: () => {
        useDialogStore
          .getState()
          .open({ kind: 'delete-workspace', id: useWorkspaceStore.getState().activeWorkspaceId });
      },
    },
    {
      id: 'workspace.switch',
      title: 'Switch Workspace…',
      group: 'Workspace',
      keywords: ['change', 'go to'],
      children: () => {
        const s = useWorkspaceStore.getState();
        return (Object.keys(s.workspaces) as WorkspaceId[]).map((id) => ({
          id: `workspace.switch.${id}`,
          title: `${s.workspaces[id]?.workspace.name ?? id}${id === s.activeWorkspaceId ? ' (current)' : ''}`,
          group: 'Workspaces',
          run: () => {
            s.switchWorkspace(id);
          },
        }));
      },
    },
  ];
};
